import SliderControl from '../sliderControl';
import {
  ATTRIBUTE_TAB_CONTENT_SETTINGS,
  FIELD_TYPES,
} from '@helpers/slider-utils.js';

const {
  i18n: { __ },
  components: { PanelBody },
  element: { useState, useEffect },
} = wp;

/**
 *
 * @param {Object}   props
 * @param {Object}   props.sliderMediaBreakPoint     {
                                                     type: 'sm',
                                                     breakPoint: 576,
                                                     slug: 'mobile',
                                                     label: 'Mobile',
                                                     }
 * @param {Object}   props.sliderSettings            Slider attributes of a specific device (mobile, tablet, tablet-landscape, desktop)
 * @param {Array}    props.slides                    Number of slides in the editor
 * @param {Function} props.setSliderSettingAttribute Function for change attributes
 * @return {HTMLElement} Returns the HTML element
 */
const TabContentFields = (props) => {
  const {
    sliderMediaBreakPoint,
    sliderSettings,
    slides,
    setSliderSettingAttribute,
    removeSliderSettingAttribute,
  } = props;
  const { type, label, slug } = sliderMediaBreakPoint;
  const sliderLength = slides.length;
  const [sliderSlidesPerView, setSlidesPerView] = useState(false);
  const [sliderPagination, setSliderPagination] = useState(false);
  const DEFAULT_SLIDES_PER_VIEW = 1;

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    sliderSettings.slidesPerView === 'auto'
      ? setSlidesPerView(true)
      : setSlidesPerView(false);

    // eslint-disable-next-line no-unused-expressions
    sliderSettings.pagination.enabled
      ? setSliderPagination(true)
      : setSliderPagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sliderSettingsFields = [
    {
      label: 'Variable Width',
      name: 'slidesPerView',
      value: sliderSlidesPerView,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSlidesPerView(value);
        const settingKey = 'slidesPerView';
        if (value === true) {
          setSliderSettingAttribute(
            ATTRIBUTE_TAB_CONTENT_SETTINGS,
            deviceSlug,
            settingKey,
            '',
            'auto',
          );
        } else {
          setSliderSettingAttribute(
            ATTRIBUTE_TAB_CONTENT_SETTINGS,
            deviceSlug,
            settingKey,
            '',
            DEFAULT_SLIDES_PER_VIEW,
          );
        }
      },
    },
    {
      label: 'Slides to Show',
      name: 'slidesPerView',
      value: sliderSettings.slidesPerView,
      type: FIELD_TYPES.Number,
      min: 1,
      mix: sliderLength,
      step: 'any',
      shouldRenderCallback() {
        return sliderSlidesPerView === false;
      },
    },
    {
      label: 'Slides to Scroll',
      name: 'slidesPerGroup',
      value: sliderSettings.slidesPerGroup,
      type: FIELD_TYPES.Range,
      min: 1,
      mix: sliderLength,
    },
    {
      label: 'Show Arrows',
      name: 'navigation',
      value: sliderSettings.navigation.enabled,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          'navigation',
          'enabled',
          value,
        );
      },
    },
    {
      label: 'Enable Pagination',
      name: 'pagination',
      value: sliderPagination,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderPagination(value);
        const settingKey = 'pagination';
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          settingKey,
          'enabled',
          value,
        );
      },
    },
    {
      label: 'Pagination Type',
      name: 'pagination',
      value: sliderSettings.pagination.type,
      options: [
        {
          label: __('Show Dots', 'b3rg'),
          value: '',
        },
        {
          label: __('Progress Bar', 'b3rg'),
          value: 'progressbar',
        },
      ],
      type: FIELD_TYPES.Select,
      onChange(deviceSlug, value) {
        const settingKey = 'type';
        if (!value) {
          removeSliderSettingAttribute(
            ATTRIBUTE_TAB_CONTENT_SETTINGS,
            deviceSlug,
            'pagination',
            settingKey,
            '',
          );
        }
        if (value && value === 'progressbar') {
          setSliderSettingAttribute(
            ATTRIBUTE_TAB_CONTENT_SETTINGS,
            deviceSlug,
            'pagination',
            settingKey,
            'progressbar',
          );
        }
      },
      shouldRenderCallback() {
        return sliderPagination === true;
      },
    },
    {
      label: 'Scroll Speed',
      name: 'speed',
      value: sliderSettings.speed,
      type: FIELD_TYPES.Range,
      min: 1,
      max: 10000,
    },
    {
      label: 'Adaptive Height',
      name: 'autoHeight',
      value: sliderSettings.autoHeight,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          'autoHeight',
          '',
          value,
        );
      },
    },
    {
      label: 'Draggable',
      name: 'allowTouchMove',
      value: sliderSettings.allowTouchMove,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          'allowTouchMove',
          '',
          value,
        );
      },
    },
    {
      label: 'Lazy Load',
      name: 'lazy',
      value: sliderSettings.lazy,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          'lazy',
          '',
          value,
        );
      },
    },
    {
      label: 'Center Mode',
      name: 'centeredSlides',
      value: sliderSettings.centeredSlides,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          'centeredSlides',
          '',
          value,
        );
      },
    },
    {
      label: 'Space Between',
      name: 'spaceBetween',
      value: sliderSettings.spaceBetween,
      type: FIELD_TYPES.Number,
      min: 0,
      step: 1,
    },
    {
      label: 'Effect',
      name: 'effect',
      options: [
        {
          label: __('Slide', 'b3rg'),
          value: 'slide',
        },
        {
          label: __('Fade', 'b3rg'),
          value: 'fade',
        },
      ],
      type: FIELD_TYPES.Select,
    },
    {
      label: 'Destroy Slider',
      name: 'destroy',
      value: sliderSettings.destroy,
      type: FIELD_TYPES.Toggle,
      onChange(deviceSlug, value) {
        setSliderSettingAttribute(
          ATTRIBUTE_TAB_CONTENT_SETTINGS,
          deviceSlug,
          'destroy',
          '',
          value,
        );
      },
    },
  ];

  return (
    <PanelBody title={label} initialOpen={false} key={type}>
      {sliderSettingsFields?.map((sliderSettingsField, fieldIndex) => {
        if (
          typeof sliderSettingsField.shouldRenderCallback === 'function' &&
          sliderSettingsField.shouldRenderCallback() === false
        ) {
          return null;
        }
        return (
          <SliderControl
            key={fieldIndex} // NOSONAR
            {...{
              attributeName: ATTRIBUTE_TAB_CONTENT_SETTINGS,
              inputFieldTypes: FIELD_TYPES,
              sliderSettingsField,
              deviceSlug: slug,
              setSliderSettingAttribute,
            }}
          />
        );
      })}
    </PanelBody>
  );
};

export default TabContentFields;
